import React from 'react';

import { observer } from 'mobx-react';

import { useParams } from 'react-router-dom';

import { IconButton, Menu, MenuItem } from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { Button } from 'components/Button';

import Record from 'ui/Record/Record';

import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import { MainHeader } from 'components/MainHeader/MainHeader';

import { CenteredCircularProgress } from 'components/Progress';

import routes from 'core/routes';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { useStore } from 'utils/hooks/store';

import { StyledTab, StyledTabs } from 'components/Tabs/styles';

import { isDynamicGroup } from 'containers/MemberGroups/utils/membership';
import MemberGroupDetailsCard from './MemberGroupDetailsCard';
import MemberGroupMembersCard from './MemberGroupMembersCard';
import GroupHistoryTab from './GroupHistoryTab';
import DeactivateGroupModal from './DeactivateGroupModal';
import ConvertToManualModal from './ConvertToManualModal';
import { DetailContainer, InactiveBanner, InactiveBannerMessage, TabPanel, TabsContainer } from './styles';

const GroupActionsMenu: React.FunctionComponent<{
  isActive: boolean;
  isDynamic: boolean;
  onDeactivate: () => void;
  onChangeToManual: () => void;
}> = observer(({ isActive, isDynamic, onDeactivate, onChangeToManual }) => {
  const { memberGroupDetails } = useStore();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const close = (): void => setAnchorEl(null);

  return (
    <>
      <IconButton aria-label="Group actions" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        {isActive && !isDynamic && (
          <MenuItem
            onClick={() => {
              close();
              memberGroupDetails.startConverting();
            }}
          >
            Change to dynamic
          </MenuItem>
        )}
        {isActive && isDynamic && (
          <MenuItem
            onClick={() => {
              close();
              onChangeToManual();
            }}
          >
            Change to manual
          </MenuItem>
        )}
        {isActive ? (
          <MenuItem
            onClick={() => {
              close();
              onDeactivate();
            }}
          >
            Deactivate group
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              close();
              memberGroupDetails.reactivate();
            }}
          >
            Reactivate
          </MenuItem>
        )}
      </Menu>
    </>
  );
});

type GroupTab = 'overview' | 'history';

const MemberGroupDetails: React.FunctionComponent = observer(() => {
  const navBarHeight = useNavBarHeight();
  const { memberGroupDetails } = useStore();
  const { groupId } = useParams<{ groupId: string }>();
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [convertToManualOpen, setConvertToManualOpen] = React.useState(false);
  const [tab, setTab] = React.useState<GroupTab>('overview');

  React.useEffect(() => {
    if (groupId) {
      memberGroupDetails.loadGroup(groupId);
    }
  }, [groupId]);

  const { group, hideMembersWhileEditing } = memberGroupDetails;

  if (!memberGroupDetails.isLoaded || (group !== null && group.uuid !== groupId)) {
    return <CenteredCircularProgress />;
  }

  if (!group) {
    return <NotFound />;
  }

  const groupsPath = routes.dashboard.settings.groups.toString();

  return (
    <Record
      key="member_group_details"
      navBarHeight={navBarHeight}
      Header={
        <MainHeader
          title={group.name}
          breadcrumbs={[
            { label: 'Organisation', link: '' },
            { label: 'Groups', link: groupsPath },
            { label: group.name, link: '' },
          ]}
          action={
            <GroupActionsMenu
              isActive={group.active}
              isDynamic={isDynamicGroup(group)}
              onDeactivate={() => setDeactivateOpen(true)}
              onChangeToManual={() => setConvertToManualOpen(true)}
            />
          }
        />
      }
      Content={() => (
        <DetailContainer>
          {!group.active && (
            <InactiveBanner>
              <InactiveBannerMessage>
                <InfoOutlinedIcon fontSize="small" />
                This group is inactive.
              </InactiveBannerMessage>
              <Button variant="text" size="small" onClick={() => memberGroupDetails.reactivate()}>
                Reactivate group
              </Button>
            </InactiveBanner>
          )}
          <TabsContainer>
            <StyledTabs
              value={tab}
              onChange={(_, value: GroupTab) => setTab(value)}
              aria-label="Group details sections"
            >
              <StyledTab label="Overview" id="overview-tab" value="overview" />
              <StyledTab label="History" id="history-tab" value="history" data-testid="group-history-tab-button" />
            </StyledTabs>
            <TabPanel>
              {tab === 'overview' && (
                <>
                  <MemberGroupDetailsCard />
                  {!hideMembersWhileEditing && <MemberGroupMembersCard />}
                </>
              )}
              {tab === 'history' && <GroupHistoryTab />}
            </TabPanel>
          </TabsContainer>
          <DeactivateGroupModal open={deactivateOpen} onClose={() => setDeactivateOpen(false)} />
          <ConvertToManualModal open={convertToManualOpen} onClose={() => setConvertToManualOpen(false)} />
        </DetailContainer>
      )}
    />
  );
});

export default MemberGroupDetails;
